import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { Checkbox, Dialog, DialogBody, DialogFooter } from '@blueprintjs/core';
import { css } from '@emotion/react';
import { SvgNmrFt } from 'cheminfo-font';
import { useCallback, useRef, useState, useMemo } from 'react';
import { Toolbar, useOnOff } from 'react-science/ui';
import { getDefaultPredictionOptions, predictSpectra, } from '../../data/PredictionManager';
import { useChartData } from '../context/ChartContext';
import { useDispatch } from '../context/DispatchContext';
import { useLogger } from '../context/LoggerContext';
import { useToaster } from '../context/ToasterContext';
import Button from '../elements/Button';
import PredictionPreferences from '../panels/predictionPanel/PredictionOptionsPanel';
import { useStateWithLocalStorage } from '../utility/LocalStorage';
const styles = css `
  background-color: white;

  .inner-content {
    flex: 1;
  }

  .warning {
    color: #c40000;
    font-weight: bold;
    font-size: 13px;
    text-align: justify;
    margin-top: 30px;
  }

  .warning-container {
    margin-top: 5px;
    display: flex;

    input {
      margin: 5px 5px 5px 0;
    }
  }

  .header {
    padding: 0;
    margin: 0 30%;
    width: 40%;
  }
`;
export function PredictSpectraModal({ onClose = () => null, molecule, }) {
    const [isOpenDialog, openDialog, closeDialog] = useOnOff(false);
    const refForm = useRef();
    const dispatch = useDispatch();
    const toaster = useToaster();
    const [predictionPreferences, setPredictionPreferences] = useStateWithLocalStorage('nmrium-prediction-preferences');
    const { isApproved: isAgree = false, ...options } = predictionPreferences;
    const [isApproved, setApproved] = useState(isAgree);
    const { toolOptions: { data: { predictionIndex }, }, } = useChartData();
    const handleSave = useCallback(() => {
        refForm.current.submitForm();
    }, []);
    const initValues = useMemo(() => {
        return Object.assign(getDefaultPredictionOptions(), options, {
            name: `Prediction ${predictionIndex + 1}`,
        });
    }, [options, predictionIndex]);
    const { logger } = useLogger();
    const submitHandler = useCallback((values) => {
        void (async () => {
            if (molecule) {
                const { molfile } = molecule;
                setPredictionPreferences({ ...values, isApproved });
                const predictedSpectra = [];
                for (const [key, value] of Object.entries(values.spectra)) {
                    if (value) {
                        predictedSpectra.push(key);
                    }
                }
                onClose();
                closeDialog();
                const hideLoading = toaster.showLoading({
                    message: `Predict ${predictedSpectra.join(',')} in progress`,
                });
                try {
                    const data = await predictSpectra(molfile, values.spectra);
                    dispatch({
                        type: 'PREDICT_SPECTRA',
                        payload: {
                            logger,
                            predictedSpectra: data.spectra,
                            options: values,
                            molecule,
                        },
                    });
                }
                catch (error) {
                    toaster.show({ message: error?.message, intent: 'danger' });
                }
                finally {
                    hideLoading();
                }
            }
        })();
    }, [
        molecule,
        setPredictionPreferences,
        isApproved,
        onClose,
        closeDialog,
        toaster,
        dispatch,
        logger,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(Toolbar, { children: _jsx(Toolbar.Item, { icon: _jsx(SvgNmrFt, {}), tooltip: "Predict spectra", onClick: openDialog }) }), _jsxs(Dialog, { isOpen: isOpenDialog, onClose: () => {
                    onClose();
                    closeDialog();
                }, style: { width: 600, maxWidth: 1000 }, title: "Prediction of NMR spectrum", children: [_jsxs(DialogBody, { css: styles, children: [_jsx(PredictionPreferences, { onSubmit: submitHandler, options: initValues, ref: refForm }), _jsx("p", { className: "warning", children: "In order to predict spectra we are calling an external service and the chemical structure will leave your browser! You should never predict spectra for confidential molecules." }), _jsx("div", { className: "warning-container", children: _jsx(Checkbox, { onChange: (value) => setApproved(value.target.checked), checked: isApproved, label: "I confirm that the chemical structure is not confidential." }) })] }), _jsx(DialogFooter, { className: "footer-container", children: _jsx(Button.Done, { onClick: handleSave, disabled: !isApproved, children: "Predict spectrum" }) })] })] }));
}
export default PredictSpectraModal;
